// external dependencies
import React from 'react'
import styled from '@emotion/styled'
import { node, bool, func, elementType, string, object } from 'prop-types'
import { useTheme } from 'emotion-theming'
import BaseModal from 'react-modal'
import { Global, css } from '@emotion/core'

//import components
import { Close as CrossButton } from '../svgs/Close'
import { isBrowser } from '@jelly/utils'

// styled components
const CloseButton = styled.div`
	position: absolute;
	right: 0;
	top: 0;
	z-index: 6;
	cursor: pointer;
	padding: ${({ padding }) => padding || '20px'};
`

const StyledModal = styled(BaseModal)`
	background: ${({ backgroundColor }) => backgroundColor};
	border-radius: ${({ borderRadius }) => borderRadius};
	margin: 0 auto;
	max-height: ${({ maxHeight }) => maxHeight};
	max-width: ${({ maxWidth }) => maxWidth};
	min-height: ${({ minHeight }) => minHeight};
	position: relative;
	overflow: auto;
	width: ${({ width }) => width};
	z-index: 20000;
	align-self: center;
	&:focus {
		outline: none;
	}
`

Modal.propTypes = {
	backgroundColor: string,
	borderRadius: string,
	children: node.isRequired,
	CloseIcon: elementType,
	closeIconColor: string,
	closeIconPadding: string,
	isModalVisible: bool.isRequired,
	maxHeight: string,
	maxWidth: string,
	minHeight: string,
	modalSelector: object,
	onRequestClose: func,
	opacity: string,
	theme: object,
	width: string,
}

/**
    The Modal component wraps content (i.e. children) that should be displayed in a modal.  It has three required values:
    children, isModalVisible, and setIsModalVisible.  isModalVisible and setIsModalVisible is expected to be a state value
    and the related state setter function.  In addition, the Modal accepts props related  to styling and includes the
    option areCloseShortcutsDisabled, which closes the modal if the user click or touches outside of the modal (default
    value is false).  Finally, the close icon can be overridden by passing the component CloseIcon as a prop to the Modal.

    EXAMPLE OF USAGE:
    const CloseIcon = () => <CrossArrow width="10px" height="10px" color="red" />
    const customRoot = typeof document !== 'undefined' ? document.getElementById('modal-root') : null

    function TestModal() {
      const [isModalVisible, setIsModalVisible] = useState(true) // set true to open Modal by default
      <Modal
        CloseIcon={CloseIcon} // optional
        areCloseShortcutsDisabled={true} // optional
        isModalVisible={isModalVisible} // required
        modalSelector={customRoot} // optional
        setIsModalVisible={setIsModalVisible} // required
      >
        <div>This is where the content of the modal goes</div>
      </Modal>
    }
*/
export function Modal({
	backgroundColor = '#fcfcfc',
	borderRadius = '0',
	children,
	CloseIcon,
	closeIconColor,
	closeIconPadding,
	isModalVisible,
	maxHeight = '90vh',
	maxWidth = '600px',
	minHeight = '300px',
	modalSelector,
	onRequestClose,
	opacity = 0.5,
	width = '90%',
}) {
	const {
		colors: { secondary },
	} = useTheme()

	if (!isBrowser() || !isModalVisible) {
		return null // if window is undefined or modal is not visible, do not create portal
	}
	let modalEl

	// append modal to modalSelector if specified. otherwise, add to document body
	if (modalSelector !== undefined) {
		modalEl = document.querySelector(modalSelector)
	} else {
		modalEl = document.createElement('div')
		document.body.appendChild(modalEl)
	}

	BaseModal.setAppElement(modalEl)

	return (
		<>
			<Global
				styles={css`
					main {
						filter: blur(2px);
					}
					.ReactModal__Overlay {
						opacity: 0;
						transition: opacity 300ms ease-in-out;
					}

					.ReactModal__Overlay--after-open {
						opacity: 1;
					}

					.ReactModal__Overlay--before-close {
						opacity: 0;
					}
					.ReactModal__Body--open {
						overflow-y: hidden;
					}
				`}
			/>
			<StyledModal
				backgroundColor={backgroundColor}
				borderRadius={borderRadius}
				closeTimeoutMS={300}
				isOpen={isModalVisible}
				maxHeight={maxHeight}
				maxWidth={maxWidth}
				minHeight={minHeight}
				width={width}
				onRequestClose={onRequestClose}
				style={{
					overlay: {
						backgroundColor: `rgba(0, 0, 0, ${opacity})`,
						zIndex: 2000,
						display: 'flex',
					},
				}}
			>
				<CloseButton onClick={() => onRequestClose(false)} padding={closeIconPadding}>
					{CloseIcon ? <CloseIcon /> : <CrossButton color={closeIconColor || secondary} />}
				</CloseButton>
				{children}
			</StyledModal>
		</>
	)
}
